import { Box, DefaultMantineColor, MantineFontSize, Overlay, Paper, Text, useMantineColorScheme } from '@mantine/core'
import { FC, ReactNode, useContext, useLayoutEffect, useMemo } from 'react'
import { StackIndexContext, useStackedAlerts } from './StackedAlertsContext'

/**
 * Estimates the height of an alert based on its height prop.
 * Used to calculate stacking offsets.
 */
const estimateAlertHeight = (height?: 'tiny' | 'small' | 'medium' | 'large'): number => {
  switch (height) {
    case 'tiny':
      return 80
    case 'small':
      return 120
    case 'medium':
      return 180
    case 'large':
      return 500
    default:
      return 80
  }
}

const AlertContainer: FC<{
  children?: ReactNode
  height?: 'tiny' | 'small' | 'medium' | 'large'
  type: 'alert' | 'message'
  alertColor?: DefaultMantineColor
  alertText?: string | ReactNode
  opacity?: 'low' | 'medium' | 'high'
  position?: 'center' | 'bottom' | 'top'
  noOverlay?: boolean
  textSize?: MantineFontSize
}> = ({
  children,
  height = 'tiny',
  type = 'message',
  alertColor,
  alertText,
  opacity = 'medium',
  position = 'center',
  noOverlay = false,
  textSize = 'var(--spacing-xl)',
}) => {
  const { colorScheme } = useMantineColorScheme()
  const color = colorScheme === 'dark' ? 'white' : 'black'
  const stackContext = useStackedAlerts()
  const stackIndex = useContext(StackIndexContext)
  const isStackedMode = stackContext !== null

  const registerAlert = stackContext?.registerAlert
  const unregisterAlert = stackContext?.unregisterAlert

  // If in stacked context, register this alert by index
  useLayoutEffect(() => {
    if (registerAlert && unregisterAlert && stackIndex !== null) {
      const estimatedHeight = estimateAlertHeight(height)
      registerAlert(stackIndex, estimatedHeight)
      return () => {
        unregisterAlert(stackIndex)
      }
    }
  }, [registerAlert, unregisterAlert, stackIndex, height])

  // Get offset from context, re-calculating when layoutVersion changes
  const bottomOffset = useMemo(() => {
    if (stackContext != null && stackIndex != null) {
      return stackContext.getOffset(stackIndex)
    }
    return undefined
    // stackContext.layoutVersion is the explicit signal that layout has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackContext, stackIndex, stackContext?.layoutVersion])

  const overlayOpacity = useMemo(() => {
    switch (opacity) {
      case 'low':
        return 0.5
      case 'medium':
        return 0.8
      case 'high':
        return 0.2
      default:
        return 0.8
    }
  }, [opacity])

  const container = useMemo(() => {
    if (type === 'alert') {
      return (
        <Paper
          withBorder
          shadow="md"
          bg={alertColor}
          p="md"
          w="75vw"
          opacity={opacity === 'high' ? 1 : 0.8}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          h={estimateAlertHeight(height)}
        >
          <Text fz={textSize} ta="center" c={color}>
            {alertText}
          </Text>
        </Paper>
      )
    }
    return (
      <Paper
        p="xl"
        w="75vw"
        h={estimateAlertHeight(height)}
        ta="center"
        fz="var(--spacing-xl)"
        style={{
          opacity: opacity === 'high' ? 1 : undefined,
        }}
      >
        {children}
      </Paper>
    )
  }, [type, children, height, alertText, alertColor, opacity, textSize, color])

  // Stacked mode: direct positioning via context
  if (isStackedMode) {
    return (
      <Box
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: bottomOffset,
          zIndex: 10000,
          pointerEvents: 'auto',
        }}
      >
        {container}
      </Box>
    )
  }

  // Standalone mode: full-viewport overlay with optional dimming
  return (
    <Box
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        zIndex: 10000,
        pointerEvents: 'none',
        ...(position === 'center' && {
          alignItems: 'center',
          justifyContent: 'center',
        }),
        ...(position === 'bottom' && {
          alignItems: 'flex-end',
          justifyContent: 'center',
          paddingBottom: 80,
        }),
        ...(position === 'top' && {
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: 20,
        }),
      }}
    >
      {!noOverlay && <Overlay opacity={overlayOpacity} />}
      <Box style={{ position: 'relative', zIndex: 10001, pointerEvents: 'auto' }}>{container}</Box>
    </Box>
  )
}

export default AlertContainer
